/**
 * Per-colour artwork grid (BLU-655 B2 — per-colour upload mode).
 *
 * The "By colour" alternative to `DesignsTier`'s set cards: one row per distinct
 * colour (or a single "All garments" row for a colour-less product), each with a
 * FRONT cell and a BACK cell. Dropping a PNG on a cell publishes it to EVERY
 * synced variant SKU of that colour + side via `useColourArtworkUpload` (the
 * mint -> PUT -> commit fan-out). Unlike the set-mode set cards, this list is
 * compact and scales to products with 10+ colours.
 *
 * Inventory-app design language (DESIGN.md): brand-purple single accent, dashed
 * dropzone + `DesignThumb` preview mirroring `VariantArtworkUploader`, amber for
 * the recoverable "some SKUs still failed" state, destructive-red only for a
 * hard client-side rejection (bad file). A colour is only "published" for a side
 * once ALL its SKUs carry a URL — a partial state shows `n/N published`.
 *
 * A cell's SKUs are the variants of that colour that have an upload target
 * (`productArtworkTarget`, over the shared `artworkTargetForVariant`): a SYNCED
 * variant, i.e. `derived.ok` AND `nativeSku === derived.sku` (the variant's
 * live Shopify SKU already equals the derived print SKU), or, for a store SKU
 * product (Phase 88), a set-up store SKU or one an upload can set up (88a.1).
 * The commit locates the Shopify
 * variant by SKU search, so an un-synced colour would always fail to publish:
 * those colours render a disabled "sync first" hint instead of a dead
 * dropzone. A variant whose store SKU changed since it was set up is never a
 * target: it renders a disabled "Store SKU changed" cell.
 *
 * Platen variants (Phase 86, D-01 / D-21): when the product's type has Platen
 * Size rules the grid gains a "Platen variant" column and each colour gets one
 * sub-row per platen variant. Each cell then uploads ONLY that colour's SKUs
 * within that platen variant's sizes, states the platen variant on every commit
 * (`expectedPlatenVariantKey`), checks the PNG's pixel size first (one dialog
 * for the whole tier, `pixelGuard`), and counts as published only under the
 * shared exact-marker test (D-22). A side counts as missing only when the
 * design prints it (the shared required-sides rule, with each SKU's dual flag):
 * a front-only design never lists its Back cells as missing. A product without
 * rules renders the three-column grid exactly as before. A surface can pass
 * `replaceNoticeFor`, a message the operator must see before a cell's upload
 * (the store SKU replace warning); it shares the pixel check's one dialog.
 *
 * Small made from Large (Phase 86 Round 4, R4-3): when the platen has a Made
 * from Large preset (`platen.pixels`), a Large cell's run that publishes asks
 * the page's derive queue (`usePageDeriveSmall`, one derive at a time for the
 * whole page, keyed by product, so collapsing a row keeps it) to make the
 * same colour's Small file on that platen. The Small cell is locked while it
 * waits and while it is made, then shows it made, kept (a Small file someone
 * uploaded always wins) or failed with Try again. No preset: nothing extra
 * runs. Such a Small cell (MUST 9: a preset, and the colour has Large sizes
 * on the same platen) shows the Made from Large chip for a made file, offers
 * "Make Small from Large" when it has no file and the colour's Large is
 * published (a quiet panel says Small is made once Large is uploaded), and
 * warns before an own file replaces a made one. Drop hints and the pixel
 * check use the org's Large px.
 *
 * Settings are defaults (R4-12). By colour, each colour decides on its own
 * (S6b): while a colour's Small is made from Large (a preset, both sizes on
 * the platen, no Small file of its own, not by hand), its Small row is
 * hidden; the Large row says "Small made from Large at 90%" with "Upload
 * Small by hand" (`SmallFromLargeLine`, compact), and each Large cell shows
 * that Small side's waiting, making, made, kept or failed state and its Make
 * Small from Large. "Upload Small by hand" shows the colour's Small row as
 * today's drop cells and no Small is made for it; the same button, now "Make
 * Small from Large", switches back and makes what it can. The choice is per
 * product, platen and colour (`platen.smallByHand.setFor`, page-level in
 * useStagedDesigns); By set's platen-wide choice counts for every colour. A
 * drop on a default Small panel turns it on before the upload. A Small file
 * someone uploaded is always kept, so its row always shows.
 *
 * Table (S6b): visible column and row lines, the colour cell spans its
 * platen rows, every cell's content centred, Front and Back in their own
 * columns. Columns shrink and wrap (`minmax(0, 1fr)`); the grid never
 * scrolls sideways. Below md each colour stacks.
 *
 * Set up on upload (88a.1, D4): a not-set-up store SKU variant the server says
 * an upload can set up is a target too (`settable`, see `productArtworkTarget`).
 * When a commit sets the product up, or tries to, every cell (here and in
 * Product updates) shows one toast saying what happened and re-reads the
 * product list and Product updates once (`useStoreSkuSetUpResult`).
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, ImageIcon, Loader2, RefreshCw, Upload } from 'lucide-react';
import {
  formatScalePercent,
  isPlatenSideCovered,
  type DtgPlatenCode,
  type EffectivePlatenPixels,
} from '@threadheads/shared';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';
import { scrollIntoViewBelowActionBar } from '@/lib/scroll-container';
import {
  ONE_SHOT_HIGHLIGHT_CLASS,
  ONE_SHOT_HIGHLIGHT_TRANSITION,
  useOneShotHighlight,
} from '@/hooks/useOneShotHighlight';
import { Button } from '@/components/ui/button';
import { SectionError } from '@/components/shared/SectionError';
import { formatPlatenSizeRange, platenGroupLabel } from '@/lib/platen-size-range';
import { ArtworkLocationGate } from './ArtworkLocationGate';
import { DesignThumb } from './DesignThumb';
import { ColourDot } from './ColourDot';
import { LockedHint } from './LockedHint';
import { DERIVE_BUTTON_CLASS, SmallFromLargeLine, SmallMadePanel } from './SmallFromLargeLine';
import { StudioEntryLink } from './StudioEntryLink';
import type { StudioFocus } from '../lib/studio-return';
import { colourSwatchHex } from '../lib/colour-swatch';
import { distinctColours, isColourless } from '../lib/status';
import {
  useColourArtworkUpload,
  type ColourUploadPlatenSkip,
  type ColourUploadSetUp,
} from '../hooks/useColourArtworkUpload';
import {
  expectedPlatenFilePixels,
  type PlatenPixelGuard,
  type PlatenUploadNotice,
} from '../hooks/usePlatenPixelCheck';
import { usePlatenFileStateRefresh } from '../hooks/usePlatenSizeContext';
import {
  usePageDeriveSmall,
  type DeriveSmallApi,
  type DeriveSmallCellState,
} from '../hooks/useDeriveSmallPlatenArtwork';
import { useCanOpenPlatenSettings } from '../hooks/usePlatenSettingsReach';
import { skusWithMadeSmall } from '../lib/replace-notice';
import type { ArtworkSide } from '../hooks/useVariantArtworkUpload';
import {
  isPlatenCellPublished,
  isPlatenSideRequired,
  isSideMadeFromLarge,
  platenDualSkus,
  platenMissingWording,
  productArtworkTarget,
  type PlatenFileStateBySku,
  type PlatenGroup,
} from '../lib/platen';
import type { PreviewProduct, PreviewVariant } from '../lib/types';

export interface ColourArtworkGridProps {
  product: PreviewProduct;
  /**
   * The resolved location the org publishes against. `null` is NOT one state —
   * read it together with `locationIsLoading` and `needsLocationChoice`:
   * pending, "pick one first", and "you have no active print location" are
   * three different things and each gets its own surface.
   */
  locationId: string | null;
  /** `locations.list` is still in flight — render a skeleton, never the empty state. */
  locationIsLoading?: boolean;
  /** Candidates exist but the operator has not picked one; uploads stay disabled. */
  needsLocationChoice?: boolean;
  admin: boolean;
  /**
   * Phase 86: the product's platen variant groups and file state, or null /
   * absent for a product whose type has no Platen Size rules (today's grid,
   * unchanged). With groups, each colour gets one sub-row per platen variant
   * and each cell uploads only that colour's SKUs in that platen variant.
   */
  platen?: ColourArtworkGridPlaten | null;
  /** The page's platen config read is still in flight: we cannot group yet. */
  platenLoading?: boolean;
}

export interface ColourArtworkGridPlaten {
  groups: PlatenGroup[];
  fileState: PlatenFileStateBySku;
  fileStateLoading: boolean;
  fileStateError: boolean;
  /** The tier's ONE pixel-size check, so only one dialog is ever open. */
  pixelGuard: PlatenPixelGuard;
  /**
   * A message the operator must see before this cell's upload, or null for
   * none (Phase 88: the first upload into a store SKU that prints from a
   * catalogue file today). It is shown in the pixel check's one dialog.
   */
  replaceNoticeFor?: (cell: PlatenReplaceCell) => PlatenReplaceNotice | null;
  /** BLU-1238 (R3-14): a run's skip platen mark answers, for the tier's notes. */
  onPlatenSkip?: (outcomes: ColourUploadPlatenSkip[]) => void;
  /**
   * BLU-1238 (R3-2): the product keeps printing its current files, so a
   * missing platen variant file is optional, never "can't be printed".
   */
  keepsCurrentFiles?: boolean;
  /**
   * Round 4: the rules org's Platen variants (each platen's Large px and its
   * Made from Large preset), from the platen context read. Absent or null:
   * no Small is ever made from Large here.
   */
  pixels?: EffectivePlatenPixels | null;
  /**
   * Round 4 (R4-12): the product's "Upload Small by hand instead" choice per
   * platen, bound to its product. Absent: Small is always made from Large
   * where it can be.
   */
  smallByHand?: SmallByHandChoice;
}

/** One product's Small by hand choice (R4-12), per platen and (S6b) per colour. */
export interface SmallByHandChoice {
  /** The platen-wide choice (By set's). */
  isOn: (platen: DtgPlatenCode) => boolean;
  set: (platen: DtgPlatenCode, on: boolean) => void;
  /** S6b: this colour row's Small is by hand (its own choice or the platen-wide one). Absent: `isOn`. */
  isOnFor?: (platen: DtgPlatenCode, rowKey: string) => boolean;
  /**
   * S6b: choose (or drop) by hand for ONE colour row. `rowKeys`: every colour
   * row with Small and Large on the platen (dropping it while the platen-wide
   * choice is on keeps the others by hand). Absent: `set`.
   */
  setFor?: (platen: DtgPlatenCode, rowKey: string, on: boolean, rowKeys: readonly string[]) => void;
}

/** One platen cell, as `replaceNoticeFor` is asked about it. */
export interface PlatenReplaceCell {
  /** The SKUs this cell uploads to. */
  skus: string[];
  side: ArtworkSide;
  group: PlatenGroup;
  /** The colour's name, or null for the colour-less "All garments" row. */
  colour: string | null;
}

export interface PlatenReplaceNotice extends PlatenUploadNotice {
  /** The operator chose Upload. */
  accept: () => void;
}

/** One colour row: label + the synced design-bearing SKUs it fans out to. */
interface ColourRow {
  key: string;
  /** Colour name for the swatch dot, or null for the colour-less "All garments" row. */
  dotName: string | null;
  label: string;
  skus: string[];
  /** Variants whose store SKU changed since it was set up (never an upload target). */
  changed: Array<{ key: string; label: string }>;
  /** Dark garment backdrop for the thumbnail (decorative, luminance heuristic). */
  dark: boolean;
}

/**
 * Decorative dark-garment heuristic off the colour swatch — a dark backdrop
 * makes white-on-dark artwork read honestly. Unknown colours default to the
 * light checkerboard. This is presentation only; it never gates upload.
 */
function isDarkColour(name: string | null): boolean {
  const hex = colourSwatchHex(name);
  if (!hex) return false;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  // Rec. 601 relative luminance, 0..255.
  const lum = 0.299 * r + 0.587 * g + 0.114 * b;
  return lum < 110;
}

/** Colour | Front | Back, for a product without platen rules. */
const PLAIN_GRID_COLS = 'md:grid-cols-[11rem_minmax(0,1fr)_minmax(0,1fr)]';
/** One table cell: its own padding, so the column and row lines run edge to edge (S6b). */
const CELL = 'min-w-0 px-3 py-3';
/** One column label. */
const HEAD_CELL = 'min-w-0 px-3 py-2';

export function ColourArtworkGrid({
  product,
  locationId,
  locationIsLoading = false,
  needsLocationChoice = false,
  admin,
  platen = null,
  platenLoading = false,
}: ColourArtworkGridProps) {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();

  // --- rows: one per colour, or a single "All garments" row --------------
  const rows = useMemo<ColourRow[]>(() => {
    const targets = (variants: PreviewVariant[]): Pick<ColourRow, 'skus' | 'changed'> => {
      const seen = new Set<string>();
      const skus: string[] = [];
      const changed: ColourRow['changed'] = [];
      for (const v of variants) {
        const target = productArtworkTarget(product, v);
        if (target.identity === 'changed') {
          changed.push({ key: v.id, label: v.nativeSku ?? v.title });
          continue;
        }
        if (target.sku && !seen.has(target.sku)) {
          seen.add(target.sku);
          skus.push(target.sku);
        }
      }
      return { skus, changed };
    };

    if (isColourless(product)) {
      return [
        {
          key: 'all',
          dotName: null,
          label: t('colourArtwork.allGarments'),
          ...targets(product.variants),
          dark: false,
        },
      ];
    }

    return distinctColours(product).map((colour) => {
      const key = colour.toLowerCase();
      const variants = product.variants.filter((v) => v.colourName?.toLowerCase() === key);
      return {
        key,
        dotName: colour,
        label: colour,
        ...targets(variants),
        dark: isDarkColour(colour),
      };
    });
  }, [product, t]);
  // A store SKU product's colour without a set-up SKU is not "unsynced": Set
  // up is what it needs.
  const noTargetHint = t(product.storeSku ? 'colourArtwork.storeSkuNotSetUpHint' : 'colourArtwork.notSyncedHint');

  // --- resting published URLs (server truth after reload) ----------------
  // One bounded query over every synced SKU on the product; each cell reads its
  // own colour + side slice. Bounded by the row derivation, never history.
  const allSkus = useMemo(() => {
    const set = new Set<string>();
    for (const row of rows) for (const sku of row.skus) set.add(sku);
    return [...set];
  }, [rows]);

  const artworkUrlsQuery = trpc.productSync.getVariantArtworkUrls.useQuery(
    { skus: allSkus },
    { enabled: allSkus.length > 0, staleTime: 30_000 },
  );
  const urlMap = artworkUrlsQuery.data?.urls;

  const invalidateResting = useCallback(() => {
    void utils.productSync.getVariantArtworkUrls.invalidate();
  }, [utils]);

  // --- platen (Phase 86): file-state refresh and a refused commit -------
  // The same stable chunks the file-state read used, so exactly this
  // product's cached chunks refetch.
  const platenSkus = useMemo(() => (platen ? platen.groups.flatMap((g) => g.skus) : []), [platen]);
  const { invalidateFileState: invalidatePlatenFileState, onPlatenCommitFailed } =
    usePlatenFileStateRefresh(platenSkus);

  const onPlatenPublishStateChange = useCallback(() => {
    invalidateResting();
    void invalidatePlatenFileState();
  }, [invalidateResting, invalidatePlatenFileState]);

  // Round 4: the page's one derive queue (Small made from Large). A job's
  // refresh is awaited before the cell shows made or kept.
  const derive = usePageDeriveSmall();
  const refreshAfterDerive = useCallback(
    () => Promise.all([utils.productSync.getVariantArtworkUrls.invalidate(), invalidatePlatenFileState()]),
    [utils, invalidatePlatenFileState],
  );

  // --- resting read: pending and failed are NOT "nothing published" -------
  // Painting empty tiles from a pending or failed read tells the operator their
  // artwork is gone. Both get their own surface (DESIGN.md §15.1 / §15.3).
  const platenPending = platenLoading || (platen !== null && platen.fileStateLoading && platenSkus.length > 0);

  const renderReady = (readyLocationId: string): ReactNode => {
    if ((artworkUrlsQuery.isLoading && allSkus.length > 0) || platenPending) {
      return (
        <div
          className="space-y-2 rounded-lg border bg-background p-3"
          role="status"
          aria-busy="true"
          aria-label={t('colourArtwork.loadingArtwork')}
        >
          {rows.map((row) => (
            <div
              key={row.key}
              className={cn(
                'grid gap-3',
                platen ? PLATEN_GRID_COLS : PLAIN_GRID_COLS,
              )}
            >
              <div className="h-5 w-28 animate-pulse rounded bg-muted motion-reduce:animate-none" />
              {platen && <div className="hidden h-5 w-24 animate-pulse rounded bg-muted motion-reduce:animate-none md:block" />}
              <div className="h-16 animate-pulse rounded bg-muted motion-reduce:animate-none" />
              <div className="h-16 animate-pulse rounded bg-muted motion-reduce:animate-none" />
            </div>
          ))}
        </div>
      );
    }

    // A failed platen file-state read is the same claim as a failed URL read:
    // painting "missing" from it would send operators to re-upload files that
    // are already there.
    if (artworkUrlsQuery.isError || (platen !== null && platen.fileStateError)) {
      return (
        <SectionError
          variant="compact"
          title={t('colourArtwork.urlsErrorTitle')}
          body={t('colourArtwork.urlsErrorBody')}
          retryLabel={t('colourArtwork.urlsRetry')}
          retrying={artworkUrlsQuery.isFetching}
          onRetry={() => {
            void artworkUrlsQuery.refetch();
            if (platen) void invalidatePlatenFileState();
          }}
        />
      );
    }

    if (platen) {
      return (
        <PlatenColourGrid
          rows={rows}
          groups={platen.groups}
          fileState={platen.fileState}
          urlMap={urlMap}
          locationId={readyLocationId}
          admin={admin}
          pixelGuard={platen.pixelGuard}
          replaceNoticeFor={platen.replaceNoticeFor}
          noTargetHint={noTargetHint}
          onPublishStateChange={onPlatenPublishStateChange}
          onPlatenCommitFailed={onPlatenCommitFailed}
          onPlatenSkip={platen.onPlatenSkip}
          keepsCurrentFiles={platen.keepsCurrentFiles === true}
          product={product}
          pixels={platen.pixels ?? null}
          derive={derive}
          refreshAfterDerive={refreshAfterDerive}
          smallByHand={platen.smallByHand}
        />
      );
    }

    const anyBackCell = rows.some((row) => row.skus.length > 0);
    return (
      <div className="space-y-2">
        <div className="overflow-hidden rounded-lg border bg-background">
          {/* Column labels, hidden on narrow viewports where rows stack. */}
          <div
            className={cn(
              'hidden border-b bg-muted/40 text-[11px] font-bold uppercase tracking-wide text-muted-foreground md:grid md:divide-x',
              PLAIN_GRID_COLS,
            )}
          >
            <span className={HEAD_CELL}>{t('colourArtwork.colourHeader')}</span>
            <span className={cn(HEAD_CELL, 'text-center')}>{t('variantArtwork.frontLabel')}</span>
            <span className={cn(HEAD_CELL, 'text-center')}>{t('variantArtwork.backLabel')}</span>
          </div>

          <div className="divide-y">
            {rows.map((row) => {
              const frontPublished = row.skus.filter((s) => urlMap?.[s]?.frontUrl).length;
              const backPublished = row.skus.filter((s) => urlMap?.[s]?.backUrl).length;
              const frontUrl = row.skus.map((s) => urlMap?.[s]?.frontUrl).find(Boolean) ?? null;
              const backUrl = row.skus.map((s) => urlMap?.[s]?.backUrl).find(Boolean) ?? null;

              return (
                <div key={row.key} className={cn('grid md:divide-x', PLAIN_GRID_COLS)}>
                  {/* Colour identity + SKU count */}
                  <div className={cn(CELL, 'flex items-center')}>
                    <ColourIdentity row={row} />
                  </div>

                  {row.skus.length === 0 && row.changed.length === 0 && (
                    // Un-synced colour: a single disabled hint across both cells.
                    <div className={cn(CELL, 'md:col-span-2')}>
                      <LockedHint>{noTargetHint}</LockedHint>
                    </div>
                  )}
                  {row.skus.length > 0 && (
                    <>
                      <ColourArtworkCell
                        side="front"
                        colourLabel={row.label}
                        skus={row.skus}
                        locationId={readyLocationId}
                        admin={admin}
                        dark={row.dark}
                        restingPublishedCount={frontPublished}
                        restingUrl={frontUrl}
                        onPublishStateChange={invalidateResting}
                        showBackNote={false}
                        product={product}
                        className={CELL}
                      />
                      <ColourArtworkCell
                        side="back"
                        colourLabel={row.label}
                        skus={row.skus}
                        locationId={readyLocationId}
                        admin={admin}
                        dark={row.dark}
                        restingPublishedCount={backPublished}
                        restingUrl={backUrl}
                        onPublishStateChange={invalidateResting}
                        showBackNote={false}
                        product={product}
                        className={CELL}
                      />
                    </>
                  )}
                  {row.changed.length > 0 && (
                    <ChangedStoreSkuCell
                      changed={row.changed}
                      ariaLabel={t('platen.cellLabel', { colour: row.label, group: t('colourArtwork.storeSkuChanged') })}
                      className={cn(CELL, 'md:col-span-2', row.skus.length > 0 && 'md:col-start-2 md:border-t')}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {/* The second-side note once for the grid, not once per row. */}
        {anyBackCell && <p className="text-[11px] text-muted-foreground">{t('variantArtwork.backSideNote')}</p>}
      </div>
    );
  };

  // --- gated: the three distinct reasons there is no target ---------------
  return (
    <ArtworkLocationGate
      isLoading={locationIsLoading}
      needsChoice={needsLocationChoice}
      locationId={locationId}
    >
      {renderReady}
    </ArtworkLocationGate>
  );
}

/** A colour's swatch, name (full name on hover when it truncates) and SKU count. */
function ColourIdentity({ row }: { row: ColourRow }) {
  const { t } = useTranslation('productSync');
  return (
    <div className="flex min-w-0 items-center gap-2">
      <ColourDot name={row.dotName} />
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-semibold" title={row.label}>
          {row.label}
        </span>
        <span className="block text-[11px] tabular-nums text-muted-foreground">
          {t('colourArtwork.skuCount', { count: row.skus.length })}
        </span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Platen variant grid (Phase 86, D-01 / D-21): Colour | Platen variant | Front
// | Back. Each colour gets one sub-row per platen variant it has synced SKUs
// in; each cell uploads ONLY that colour's SKUs within that platen variant's
// sizes, so a Small file never reaches a Large size.
// ---------------------------------------------------------------------------

const PLATEN_GRID_COLS = 'md:grid-cols-[8rem_10rem_minmax(0,1fr)_minmax(0,1fr)]';

/**
 * The disabled cell for variants whose store SKU changed since it was set up
 * (Phase 88): never an upload target, so no dropzone. Names the live SKUs, so
 * the operator can find them in the store, and says what to do next (the same
 * next step Verify gives for a set-up SKU that no longer matches the store).
 */
function ChangedStoreSkuCell({
  changed,
  ariaLabel,
  className,
}: {
  changed: ColourRow['changed'];
  ariaLabel: string;
  className?: string;
}) {
  const { t } = useTranslation('productSync');
  return (
    <div role="group" aria-disabled="true" aria-label={ariaLabel} className={className}>
      <LockedHint>
        <p className="font-medium">{t('colourArtwork.storeSkuChanged')}</p>
        <ul className="mt-0.5 space-y-0.5">
          {changed.map((item) => (
            <li key={item.key} className="break-all font-mono">
              {item.label}
            </li>
          ))}
        </ul>
        <p className="mt-1">{t('colourArtwork.storeSkuChangedNextStep')}</p>
      </LockedHint>
    </div>
  );
}

/** One "platen info wasn't written" toast at a time, however many cells warned. */
const PLATEN_INFO_WARNING_TOAST_ID = 'platen-info-warning';

/** The product a cell uploads into, for the set-up toast (88a.1, D4). */
export interface ColourArtworkCellProduct {
  /** The Shopify product id. */
  id: string;
  title: string;
}

/**
 * One "set-up did not finish" warning per product; it stays until dismissed,
 * so a second product's failure never replaces the first.
 */
function setUpFailedToastId(productId: string): string {
  return `store-sku-set-up-failed:${productId}`;
}

/**
 * What a cell does with a drop's set-up answer (88a.1, D4): one toast, then
 * one re-read of the product list and Product updates, so the product shows
 * as set up (or still waiting) without a reload.
 *  - Set up: how many store SKUs were set up and, above 0, how many variants
 *    still wait for their file.
 *  - Nothing set up yet but variants still wait (a front file on a two-sided
 *    size): a plain note that these sizes need their other side's file.
 *  - Nothing set up and nothing waiting: another run set it up first.
 *  - Failed: a warning naming the product that stays until dismissed, pointing
 *    to Set up store SKUs in the Products list (Product updates has no such
 *    button).
 *  - Store SKUs turned off meanwhile: the admin's own choice, so a plain timed
 *    note, never a warning.
 */
function useStoreSkuSetUpResult(product: ColourArtworkCellProduct): (setUp: ColourUploadSetUp) => void {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();
  const { id: productId, title: productTitle } = product;
  return useCallback(
    (setUp: ColourUploadSetUp) => {
      if (setUp.status === 'failed') {
        if (setUp.reason === 'store_skus_off') {
          toast.info(t('colourArtwork.setUpStoreSkusOff'));
        } else {
          toast.warning(t('colourArtwork.setUpFailed', { product: productTitle }), {
            id: setUpFailedToastId(productId),
            duration: Infinity,
            // A long product title wraps inside the toast.
            className: 'break-words',
          });
        }
      } else {
        const waiting =
          setUp.waitingForFile > 0
            ? { description: t('colourArtwork.setUpWaiting', { count: setUp.waitingForFile }) }
            : {};
        if (setUp.declared > 0) toast.success(t('colourArtwork.setUpDone', { count: setUp.declared }), waiting);
        else if (setUp.waitingForFile > 0) toast.info(t('colourArtwork.setUpNothingYet'), waiting);
        else toast.success(t('colourArtwork.setUpAlready'), waiting);
      }
      void utils.productSync.getProductSyncPreview.invalidate();
      void utils.productUpdates.getPage.invalidate();
    },
    [t, utils, productId, productTitle],
  );
}

interface PlatenColourGridProps {
  rows: ColourRow[];
  groups: PlatenGroup[];
  fileState: PlatenFileStateBySku;
  urlMap: Record<string, { frontUrl: string | null; backUrl: string | null }> | undefined;
  locationId: string;
  admin: boolean;
  pixelGuard: PlatenPixelGuard;
  replaceNoticeFor?: ColourArtworkGridPlaten['replaceNoticeFor'];
  /** What a colour with no upload target says (not synced, or no store SKU set up). */
  noTargetHint: string;
  onPublishStateChange: () => void;
  onPlatenCommitFailed: (message: string) => void;
  onPlatenSkip?: ColourArtworkGridPlaten['onPlatenSkip'];
  keepsCurrentFiles: boolean;
  product: ColourArtworkCellProduct;
  pixels: EffectivePlatenPixels | null;
  derive: DeriveSmallApi;
  refreshAfterDerive: () => Promise<unknown>;
  smallByHand?: SmallByHandChoice;
}

const SIDES: ArtworkSide[] = ['front', 'back'];

/** The link flag for one side of one SKU: does a file exist that keeps printing? */
function hasSideFile(fileState: PlatenFileStateBySku, sku: string, side: ArtworkSide): boolean {
  const state = fileState[sku];
  return side === 'front' ? state?.hasFrontUrl === true : state?.hasBackUrl === true;
}

function PlatenColourGrid({
  rows,
  groups,
  fileState,
  urlMap,
  locationId,
  admin,
  pixelGuard,
  replaceNoticeFor,
  noTargetHint,
  onPublishStateChange,
  onPlatenCommitFailed,
  onPlatenSkip,
  keepsCurrentFiles,
  product,
  pixels,
  derive,
  refreshAfterDerive,
  smallByHand,
}: PlatenColourGridProps) {
  const { t } = useTranslation('productSync');
  // The SKUs that print both sides (a declared dual store SKU): they owe a
  // back file even before they have one, as the worklist and print time say.
  const dualSkus = useMemo(() => platenDualSkus(groups), [groups]);

  // One colour's SKUs in one platen variant. The colour-less "All garments"
  // row takes the whole group.
  const skusFor = (row: ColourRow, group: PlatenGroup): string[] =>
    row.key === 'all' ? group.skus : (group.skusByColour[row.key] ?? []);


  const coveredCount = (skus: string[], side: ArtworkSide, group: PlatenGroup): number =>
    skus.filter((sku) => isPlatenSideCovered(group.assignment, fileState[sku]?.[side] ?? null)).length;

  const anyBackCell = rows.some((row) => groups.some((group) => skusFor(row, group).length > 0));

  // Round 4 (R4-3): the platen's Made from Large preset, or null (no preset:
  // every Small is uploaded as today).
  const presetFor = (platenCode: DtgPlatenCode): number | null => pixels?.[platenCode]?.smallScaleBp ?? null;
  const groupOn = (platenCode: DtgPlatenCode, variant: 'Small' | 'Large'): PlatenGroup | undefined =>
    groups.find((g) => g.assignment.platen === platenCode && g.assignment.platenVariant === variant);

  // S6b: the Small by hand choice for ONE colour row (the platen-wide one,
  // By set's, counts for every colour). Without a colour-aware choice wired,
  // the platen-wide one is the colour's.
  const byHandFor = (row: ColourRow, platenCode: DtgPlatenCode): boolean =>
    smallByHand?.isOnFor ? smallByHand.isOnFor(platenCode, row.key) : (smallByHand?.isOn(platenCode) ?? false);

  /** MUST 9: this colour has Small and Large sizes on the platen, and the platen a preset. */
  const madeFromLargeOn = (row: ColourRow, platenCode: DtgPlatenCode): boolean => {
    if (presetFor(platenCode) === null) return false;
    const small = groupOn(platenCode, 'Small');
    const large = groupOn(platenCode, 'Large');
    return !!small && !!large && skusFor(row, small).length > 0 && skusFor(row, large).length > 0;
  };

  /** Every colour row whose Small on the platen is made from Large by default. */
  const madeRowKeysOn = (platenCode: DtgPlatenCode): string[] =>
    rows.filter((row) => madeFromLargeOn(row, platenCode)).map((row) => row.key);

  const setByHandFor = smallByHand
    ? (row: ColourRow, platenCode: DtgPlatenCode, on: boolean) => {
        if (smallByHand.setFor) smallByHand.setFor(platenCode, row.key, on, madeRowKeysOn(platenCode));
        else smallByHand.set(platenCode, on);
      }
    : undefined;

  /**
   * A Large cell of `row` published `published` on `side`: make the colour's
   * Small file on the same platen from it, when the platen has a preset and
   * the colour has Small sizes there. Read when the run settles, so a preset
   * or a page change made meanwhile applies.
   */
  const deriveAfterLarge = (row: ColourRow, platenCode: DtgPlatenCode, side: ArtworkSide, published: string[]) => {
    if (presetFor(platenCode) === null || published.length === 0) return;
    // R4-12: read at enqueue time, so a choice made during the upload applies.
    if (byHandFor(row, platenCode)) return;
    const small = groupOn(platenCode, 'Small');
    if (!small) return;
    const smallSkus = skusFor(row, small);
    if (smallSkus.length === 0) return;
    derive.enqueue({
      productId: product.id,
      rowKey: row.key,
      platen: platenCode,
      side,
      locationId,
      skus: smallSkus,
      sourceSku: published[0]!,
      refresh: refreshAfterDerive,
    });
  };

  /** A Small file someone uploaded is published on this side (R4-4: always kept). */
  const ownSmallFile = (skus: string[], group: PlatenGroup, side: ArtworkSide): boolean =>
    skus.some(
      (sku) =>
        isPlatenSideCovered(group.assignment, fileState[sku]?.[side] ?? null) && !isSideMadeFromLarge(fileState[sku], side),
    );

  /**
   * MUST 9: a Small cell is made from Large only when its platen has a preset
   * AND this colour has a Large group on the same platen. Otherwise it is
   * today's plain Small upload cell.
   */
  const smallFromLargeFor = (row: ColourRow, group: PlatenGroup, side: ArtworkSide): SmallFromLargeCell | undefined => {
    if (group.assignment.platenVariant !== 'Small') return undefined;
    const platenCode = group.assignment.platen;
    if (!madeFromLargeOn(row, platenCode)) return undefined;
    const large = groupOn(platenCode, 'Large')!;
    const largeSkus = skusFor(row, large);
    const smallSkus = skusFor(row, group);
    // Make Small from Large: this Small side has no file yet and the colour's
    // Large is published on it (the first covered SKU is the source).
    const source = largeSkus.find((sku) => isPlatenSideCovered(large.assignment, fileState[sku]?.[side] ?? null));
    const empty = coveredCount(smallSkus, side, group) === 0;
    return {
      platen: platenCode,
      made: smallSkus.some((sku) => isSideMadeFromLarge(fileState[sku], side)),
      // A Small file someone uploaded: the cell says it is kept, and offers no switch back.
      ownFile: ownSmallFile(smallSkus, group, side),
      largePublished: source !== undefined,
      byHand: byHandFor(row, platenCode),
      ...(setByHandFor ? { onByHand: (on: boolean) => setByHandFor(row, platenCode, on) } : {}),
      state: derive.stateFor(product.id, row.key, platenCode, side),
      onRetry: () => derive.retry(product.id, row.key, platenCode, side),
      ...(source && empty
        ? {
            onMake: () =>
              derive.enqueue({
                productId: product.id,
                rowKey: row.key,
                platen: platenCode,
                side,
                locationId,
                skus: smallSkus,
                sourceSku: source,
                refresh: refreshAfterDerive,
              }),
          }
        : {}),
    };
  };

  /** How many sides of the colour's Small on the platen are its own (uploaded) file: 0, 1 or 2. */
  const ownSmallSidesFor = (row: ColourRow, platenCode: DtgPlatenCode): number => {
    const small = groupOn(platenCode, 'Small');
    if (!small) return 0;
    const smallSkus = skusFor(row, small);
    return SIDES.filter((side) => ownSmallFile(smallSkus, small, side)).length;
  };

  /**
   * S6b: the colour's Small row on the platen is hidden while its Small is
   * made from Large: not by hand, and no Small file of its own on either
   * side (an uploaded Small always shows).
   */
  const smallHiddenFor = (row: ColourRow, platenCode: DtgPlatenCode): boolean => {
    if (!madeFromLargeOn(row, platenCode) || byHandFor(row, platenCode)) return false;
    return ownSmallSidesFor(row, platenCode) === 0;
  };

  // Every (colour, platen variant, side) cell the design prints (the shared
  // required-sides rule, the same one the Product updates list uses) that is
  // not fully published under the SHARED coverage test (D-22). An older
  // generic file does not count as published, but under D-14 it keeps
  // printing until replaced, so the notice says which cells have no file at
  // all (those sizes cannot print) and which only have an older one.
  // BLU-1238 (R3-2): a kept product's missing cells are optional, never held.
  // S6b: a colour's hidden Small (made from Large, no row) is not counted:
  // its Large cell speaks for it.
  let missing = 0;
  let noFile = 0;
  let optional = 0;
  for (const row of rows) {
    for (const group of groups) {
      const skus = skusFor(row, group);
      if (skus.length === 0) continue;
      if (group.assignment.platenVariant === 'Small' && smallHiddenFor(row, group.assignment.platen)) continue;
      for (const side of SIDES) {
        if (!isPlatenSideRequired(skus, side, fileState, dualSkus)) continue;
        if (isPlatenCellPublished(skus, side, group.assignment, fileState)) continue;
        missing += 1;
        const uncovered = skus.filter((sku) => !isPlatenSideCovered(group.assignment, fileState[sku]?.[side] ?? null));
        const wording = platenMissingWording(uncovered.every((sku) => hasSideFile(fileState, sku, side)), keepsCurrentFiles);
        if (wording === 'noFile') noFile += 1;
        else if (wording === 'optional') optional += 1;
      }
    }
  }
  const keeps = missing - noFile - optional;

  /** Switching back for one colour: make each Small side of it that can be made now. */
  const makeColourOn = (row: ColourRow, platenCode: DtgPlatenCode) => {
    const small = groupOn(platenCode, 'Small');
    if (!small) return;
    for (const side of SIDES) {
      const cell = smallFromLargeFor(row, small, side);
      if (cell?.onMake && cell.state.phase !== 'queued' && cell.state.phase !== 'making') cell.onMake();
    }
  };

  /** R4-4: before an own file replaces a Small made from Large, the cell says it is kept from then on. */
  const madeReplaceNotice = (skus: string[], side: ArtworkSide): PlatenReplaceNotice | null =>
    skusWithMadeSmall(skus, side, fileState).length > 0
      ? { title: t('platen.derive.replaceTitle'), body: t('platen.derive.replaceBody'), accept: () => undefined }
      : null;

  // S6c: Back from the studio lands on the Large row it was opened from:
  // brought into view below the action bar and tinted once.
  const { highlighted, highlight } = useOneShotHighlight<string>();
  const onStudioReturn = (rowId: string) => (link: HTMLAnchorElement) => {
    scrollIntoViewBelowActionBar(link.closest('[data-platen-row]') ?? link);
    highlight(rowId);
  };

  return (
    <div className="space-y-3">
      {missing > 0 && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" aria-hidden="true" />
          <div className="space-y-0.5 tabular-nums">
            <p className="font-medium">{t('platen.missingNotice', { count: missing })}</p>
            {noFile > 0 && <p className="text-xs">{t('platen.missingNoticeNoFile', { count: noFile })}</p>}
            {keeps > 0 && <p className="text-xs">{t('platen.missingNoticeKeeps')}</p>}
            {optional > 0 && <p className="text-xs">{t('platen.missingNoticeOptional')}</p>}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border bg-background">
        {/* Column labels, hidden on narrow viewports where rows stack. */}
        <div
          className={cn(
            'hidden border-b bg-muted/40 text-[11px] font-bold uppercase tracking-wide text-muted-foreground md:grid md:divide-x',
            PLATEN_GRID_COLS,
          )}
        >
          <span className={HEAD_CELL}>{t('colourArtwork.colourHeader')}</span>
          <span className={cn(HEAD_CELL, 'text-center')}>{t('platen.columnHeader')}</span>
          <span className={cn(HEAD_CELL, 'text-center')}>{t('variantArtwork.frontLabel')}</span>
          <span className={cn(HEAD_CELL, 'text-center')}>{t('variantArtwork.backLabel')}</span>
        </div>

        <div className="divide-y">
          {rows.map((row) => {
            const identity = <ColourIdentity row={row} />;
            /** The colour column: the identity on the colour's first row; empty (one merged cell) after it. */
            const colourCell = (first: boolean) => (
              <div className={cn(CELL, 'items-center', first ? 'flex' : 'hidden md:flex')} aria-hidden={first ? undefined : true}>
                {first ? identity : null}
              </div>
            );

            // Variants whose store SKU changed: one disabled sub-row under the
            // colour's platen variants (or the colour's only row).
            const changedSubRow = (first: boolean) =>
              row.changed.length > 0 ? (
                <div
                  key="store-sku-changed"
                  className={cn('grid md:divide-x', PLATEN_GRID_COLS, !first && 'border-t border-dashed md:border-t-0')}
                >
                  {colourCell(first)}
                  <div className={cn(CELL, 'hidden md:block', !first && 'md:border-t')} aria-hidden="true" />
                  <ChangedStoreSkuCell
                    changed={row.changed}
                    ariaLabel={t('platen.cellLabel', { colour: row.label, group: t('colourArtwork.storeSkuChanged') })}
                    className={cn(CELL, 'md:col-span-2', !first && 'md:border-t')}
                  />
                </div>
              ) : null;

            if (row.skus.length === 0) {
              if (row.changed.length > 0) return <div key={row.key}>{changedSubRow(true)}</div>;
              return (
                <div key={row.key} className={cn('grid md:divide-x', PLATEN_GRID_COLS)}>
                  {colourCell(true)}
                  <div className={cn(CELL, 'md:col-span-3')}>
                    <LockedHint>{noTargetHint}</LockedHint>
                  </div>
                </div>
              );
            }

            const subRows = groups.filter(
              (group) =>
                skusFor(row, group).length > 0 &&
                !(group.assignment.platenVariant === 'Small' && smallHiddenFor(row, group.assignment.platen)),
            );
            if (subRows.length === 0) {
              // Synced, but none of this colour's sizes has a platen yet: the
              // missing-size prompt above the grid is where they get one.
              return (
                <div key={row.key}>
                  <div className={cn('grid md:divide-x', PLATEN_GRID_COLS)}>
                    {colourCell(true)}
                    <div className={cn(CELL, 'md:col-span-3')}>
                      <LockedHint>{t('platen.noPlatenHint')}</LockedHint>
                    </div>
                  </div>
                  {changedSubRow(false)}
                </div>
              );
            }

            return (
              <div key={row.key}>
                {subRows.map((group, index) => {
                  const skus = skusFor(row, group);
                  const platenCode = group.assignment.platen;
                  const isLarge = group.assignment.platenVariant === 'Large';
                  const name = platenGroupLabel(t, group.assignment);
                  const cellLabel = t('platen.cellLabel', { colour: row.label, group: name });
                  const rowId = `${row.key}|${group.key}`;
                  // S6b: the Large row speaks for the colour's Small on this
                  // platen: the compact made from Large line, and (while the
                  // Small row is hidden) each side's Small state.
                  const speaksForSmall = isLarge && madeFromLargeOn(row, platenCode);
                  const smallHidden = speaksForSmall && smallHiddenFor(row, platenCode);
                  const smallGroup = smallHidden ? groupOn(platenCode, 'Small') : undefined;
                  // A later row of the same colour: on md+ the row line runs
                  // from the platen column on, so the colour cell reads as
                  // one; stacked, one dashed hairline between platen rows.
                  const rowLine = index > 0 && 'md:border-t';
                  return (
                    <div
                      key={group.key}
                      data-platen-row={rowId}
                      className={cn(
                        'grid md:divide-x',
                        PLATEN_GRID_COLS,
                        index > 0 && 'border-t border-dashed md:border-t-0',
                        ONE_SHOT_HIGHLIGHT_TRANSITION,
                        highlighted === rowId && ONE_SHOT_HIGHLIGHT_CLASS,
                      )}
                    >
                      {colourCell(index === 0)}

                      <div
                        className={cn(
                          CELL,
                          'flex flex-col justify-center gap-2 md:items-center md:text-center',
                          rowLine,
                        )}
                      >
                        <div>
                          <span className="block text-xs font-semibold">{name}</span>
                          <span className="block text-[11px] leading-4 text-muted-foreground">
                            {t('platen.groupRange', { range: formatPlatenSizeRange(t, group.sizes) })}
                          </span>
                        </div>
                        {speaksForSmall && (
                          <SmallFromLargeLine
                            variant="compact"
                            platen={platenCode}
                            colour={row.label}
                            ownSmall={ownSmallSidesFor(row, platenCode)}
                            scalePercent={formatScalePercent(presetFor(platenCode)!)}
                            byHand={byHandFor(row, platenCode)}
                            {...(setByHandFor ? { onByHand: (on: boolean) => setByHandFor(row, platenCode, on) } : {})}
                            onMakeAll={() => makeColourOn(row, platenCode)}
                            admin={admin}
                          />
                        )}
                      </div>

                      {SIDES.map((side) => {
                        const urlKey = side === 'front' ? 'frontUrl' : 'backUrl';
                        const focus: StudioFocus = {
                          productId: product.id,
                          colour: row.key,
                          platenVariantKey: group.key,
                          side,
                        };
                        return (
                          <ColourArtworkCell
                            key={side}
                            side={side}
                            colourLabel={cellLabel}
                            skus={skus}
                            locationId={locationId}
                            admin={admin}
                            dark={row.dark}
                            restingPublishedCount={skus.filter((sku) => urlMap?.[sku]?.[urlKey]).length}
                            publishedOverride={coveredCount(skus, side, group)}
                            restingUrl={skus.map((sku) => urlMap?.[sku]?.[urlKey]).find(Boolean) ?? null}
                            onPublishStateChange={onPublishStateChange}
                            expectedPlatenVariantKey={group.key}
                            platen={platenCode}
                            pixelGuard={pixelGuard}
                            filePixels={expectedPlatenFilePixels(pixels, platenCode)}
                            replaceNotice={() =>
                              replaceNoticeFor?.({
                                skus,
                                side,
                                group,
                                colour: row.key === 'all' ? null : row.label,
                              }) ?? madeReplaceNotice(skus, side)
                            }
                            required={isPlatenSideRequired(skus, side, fileState, dualSkus)}
                            onPlatenCommitFailed={onPlatenCommitFailed}
                            onPlatenSkip={onPlatenSkip}
                            showBackNote={false}
                            product={product}
                            onRunSettled={
                              isLarge ? (published) => deriveAfterLarge(row, platenCode, side, published) : undefined
                            }
                            smallFromLarge={smallFromLargeFor(row, group, side)}
                            madeSmall={smallGroup ? smallFromLargeFor(row, smallGroup, side) : undefined}
                            placeOnPlaten={isLarge ? { focus, onReturn: onStudioReturn(rowId) } : undefined}
                            className={cn(CELL, rowLine)}
                          />
                        );
                      })}
                    </div>
                  );
                })}
                {changedSubRow(false)}
              </div>
            );
          })}
        </div>
      </div>
      {/* The second-side note once for the grid, not once per sub-row. */}
      {anyBackCell && <p className="text-[11px] text-muted-foreground">{t('variantArtwork.backSideNote')}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One colour + side cell — the fan-out state machine + all six display states.
// ---------------------------------------------------------------------------

/**
 * A platen cell names its platen and the tier's pixel-size check together, so
 * a platen cell can never skip the check (D-13) and the tier keeps ONE dialog.
 */
type ColourArtworkCellPlatenProps =
  | { platen?: undefined; pixelGuard?: undefined; replaceNotice?: undefined; filePixels?: undefined }
  | {
      /** The cell's platen: a dropped PNG must be exactly this platen at 300 DPI. */
      platen: DtgPlatenCode;
      /** The tier's one pixel-size check (`usePlatenPixelCheck().guard`). */
      pixelGuard: PlatenPixelGuard;
      /**
       * Round 4 (R4-5): the px the drop hint states (the org's Large numbers,
       * `expectedPlatenFilePixels`). Absent: today's table.
       */
      filePixels?: { width: number; height: number } | null;
      /**
       * Asked on each drop: a message the operator must see before this
       * upload (shown in the pixel check's one dialog), or null for none.
       */
      replaceNotice?: () => PlatenReplaceNotice | null;
    };

type ColourArtworkCellProps = ColourArtworkCellPlatenProps & {
  side: ArtworkSide;
  colourLabel: string;
  skus: string[];
  locationId: string;
  admin: boolean;
  dark: boolean;
  /** How many of this colour's SKUs already carry a URL for this side (server). */
  restingPublishedCount: number;
  /** A published URL for this side (for the resting thumbnail), if any. */
  restingUrl: string | null;
  /** Called when a run settles with publishes, so the grid refetches the truth. */
  onPublishStateChange: () => void;
  /**
   * Phase 86: the platen variant this cell's SKUs were grouped under
   * (`14x16:Small`). Sent on every commit so the server refuses a SKU whose
   * assignment changed since the page loaded. Absent for a product without
   * rules.
   */
  expectedPlatenVariantKey?: string;
  /**
   * The published count to show instead of `restingPublishedCount`: for a
   * platen cell, the SKUs whose side is covered by an exact marker on this
   * platen variant (D-22), not merely the SKUs that carry a URL.
   */
  publishedOverride?: number;
  /** Once per run, after this platen cell stopped at a failed commit. */
  onPlatenCommitFailed?: (message: string) => void;
  /** BLU-1238 (R3-14): once per run, the commits' skip platen mark answers. */
  onPlatenSkip?: (outcomes: ColourUploadPlatenSkip[]) => void;
  /**
   * Whether the design prints this side (the shared required-sides rule). A
   * side that is not required and has nothing uploaded shows "Optional", not
   * "Not uploaded", so a front-only design's Back cells never read as missing.
   * Default true.
   */
  required?: boolean;
  /**
   * Show the second-side note under a Back cell. A grid that renders the
   * note once for all its rows passes false. Default true.
   */
  showBackNote?: boolean;
  /** The product these SKUs belong to: the set-up toast names it (88a.1, D4). */
  product: ColourArtworkCellProduct;
  /**
   * Phase 91: a Large cell links to the Upload Studio ("Place on platen").
   * `focus` is the cell Back brings into view (S6c); `onReturn` brings its
   * row into view when Back lands.
   */
  placeOnPlaten?: { focus: StudioFocus; onReturn?: (link: HTMLAnchorElement) => void };
  /** Round 4: the run's published SKUs, once it settles with any (a Large cell asks for its Small). */
  onRunSettled?: (publishedSkus: string[]) => void;
  /** Round 4: this Small cell is made from Large (MUST 9); absent for every other cell. */
  smallFromLarge?: SmallFromLargeCell;
  /**
   * S6b: a Large cell whose colour's Small row is hidden (made from Large)
   * speaks for that Small side: waiting, making, made, kept, failed with Try
   * again, and Make Small from Large. Its own drop is never locked by it.
   */
  madeSmall?: SmallFromLargeCell;
  /** The table cell's own classes (padding, row line). */
  className?: string;
};

/**
 * A Small cell made from Large (MUST 9): its own per-side state and actions.
 * The by-hand choice itself is on the platen's `SmallFromLargeLine`.
 */
export interface SmallFromLargeCell {
  platen: DtgPlatenCode;
  /** The file state says this side's Small was made from Large (the chip). */
  made: boolean;
  /** A Small file someone uploaded is published on this side (kept, R4-4). */
  ownFile: boolean;
  /** The colour's Large is published on this side (else there is nothing to make Small from). */
  largePublished: boolean;
  /** R4-12: this product uploads its Small by hand on this platen. */
  byHand: boolean;
  /** R4-12: a drop on a default Small cell turns by hand on (absent: no choice wired). */
  onByHand?: (on: boolean) => void;
  state: DeriveSmallCellState;
  /** Run the last derive again (after it failed). */
  onRetry: () => void;
  /** Present when the side has no Small file and the colour's Large is published. */
  onMake?: () => void;
}

export function ColourArtworkCell({
  side,
  colourLabel,
  skus,
  locationId,
  admin,
  dark,
  restingPublishedCount: urlPublishedCount,
  restingUrl,
  onPublishStateChange,
  expectedPlatenVariantKey,
  platen,
  pixelGuard,
  replaceNotice,
  filePixels,
  publishedOverride,
  onPlatenCommitFailed,
  onPlatenSkip,
  required = true,
  showBackNote = true,
  product,
  onRunSettled,
  smallFromLarge,
  madeSmall,
  placeOnPlaten,
  className,
}: ColourArtworkCellProps) {
  const { t } = useTranslation('productSync');
  // Where the skipped fields are set up: Settings, Platen size, unless the org
  // cannot open Production settings (then Settings, Integrations).
  const canOpenPlatenSettings = useCanOpenPlatenSettings();
  const onWarnings = useCallback(() => {
    // One toast whatever fields were skipped (D-23), shared by every cell: a
    // fixed id, so ten cells uploading at once still show one warning.
    toast.warning(t(canOpenPlatenSettings ? 'platen.infoWarning' : 'platen.infoWarningClient'), {
      id: PLATEN_INFO_WARNING_TOAST_ID,
    });
  }, [t, canOpenPlatenSettings]);
  const onSetUp = useStoreSkuSetUpResult(product);
  const { state, doneCount, failedSkus, progress, upload, retryFailed, reset } = useColourArtworkUpload({
    locationId,
    side,
    skus,
    expectedPlatenVariantKey,
    onWarnings,
    onPlatenCommitFailed,
    onSetUp,
    onPlatenSkip,
    onRunSettled,
  });
  // A file this cell refused before the upload started (its pixel size could
  // not be read). Shown through the same inline hard-error block as the
  // upload hook's own validation errors.
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputId = useId();
  const restingPublishedCount = publishedOverride ?? urlPublishedCount;

  const total = skus.length;
  const sideLabel = side === 'front' ? t('variantArtwork.frontLabel') : t('variantArtwork.backLabel');

  // Refetch the resting URLs once a run settles with any publish, so the server
  // truth (n/N published) catches up with what just landed.
  const prevPhaseRef = useRef(state.phase);
  useEffect(() => {
    const prev = prevPhaseRef.current;
    if (prev !== state.phase && (state.phase === 'success' || state.phase === 'partial')) {
      onPublishStateChange();
    }
    prevPhaseRef.current = state.phase;
  }, [state.phase, onPublishStateChange]);

  const busy = state.phase === 'hashing' || state.phase === 'running';
  const isPartial = state.phase === 'partial';
  const isSuccess = state.phase === 'success';
  const isHardError = state.phase === 'error' || fileError !== null;
  const hardErrorMessage = fileError ?? state.error?.message ?? null;
  const restingFull = restingPublishedCount >= total && total > 0;
  const restingPartial = restingPublishedCount > 0 && restingPublishedCount < total;

  const thumbUrl = state.previewUrl ?? restingUrl;
  // Published count to surface: live once a run is under way, else server truth.
  const shownPublished = state.phase === 'idle' ? restingPublishedCount : doneCount;

  // Round 4: the Small made from Large this cell speaks for: its own (a
  // Small cell) or, S6b, its colour's hidden Small row (a Large cell).
  const derived = smallFromLarge ?? madeSmall;
  const derivePhase = derived?.state.phase ?? 'idle';
  const deriveBusy = derivePhase === 'queued' || derivePhase === 'making';
  // A Small waiting to be made, or being made, from Large is locked: no
  // drop, no file picker. A Large cell's own drop never is.
  const deriveLocked = !!smallFromLarge && deriveBusy;
  // Focus never drops to the page: Make stays mounted while it runs, and
  // when it goes (or after Try again) focus moves to Make or the cell.
  const sectionRef = useRef<HTMLElement>(null);
  const makeRef = useRef<HTMLButtonElement>(null);
  const makeFocusedRef = useRef(false);
  const focusAfterRetryRef = useRef(false);

  const onFile = async (file: File | null) => {
    if (!file || !admin || deriveLocked) return;
    setFileError(null);
    // Platen cells check the PNG's pixel size BEFORE hashing (D-13). A file
    // that is not a PNG by type skips the check so the upload's own "PNG only"
    // message still shows.
    const looksPng = file.type ? file.type === 'image/png' : file.name.toLowerCase().endsWith('.png');
    if (platen && looksPng) {
      // One dialog per drop: a replace notice rides in the pixel check's
      // dialog, never in a second one after it.
      const notice = replaceNotice?.() ?? null;
      const answer = await pixelGuard(file, platen, notice ? { title: notice.title, body: notice.body } : undefined);
      if (answer === 'unreadable') {
        reset();
        setFileError(t('platen.unreadablePng'));
        return;
      }
      if (answer === 'cancel') return;
      notice?.accept();
    }
    // R4-12: a file dropped on a Small made from Large is the operator's own
    // Small: no Small is made from Large for this product and platen any more.
    if (smallFromLarge && !smallFromLarge.byHand) smallFromLarge.onByHand?.(true);
    void upload(file);
  };

  // --- status chip (header) ----------------------------------------------
  const statusChip = (() => {
    if (busy) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
          <Loader2 className="h-2.5 w-2.5 animate-spin" aria-hidden="true" />
          {state.phase === 'hashing' ? t('variantArtwork.hashing') : t('variantArtwork.publishing')}
        </span>
      );
    }
    if (isSuccess || restingFull) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 ring-1 ring-emerald-300/80">
          <CheckCircle2 className="h-2.5 w-2.5" aria-hidden="true" />
          {t('variantArtwork.statusPublished')}
        </span>
      );
    }
    if (isPartial || restingPartial) {
      return (
        <span className="inline-flex items-center rounded-md bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 ring-1 ring-amber-300/80">
          {t('colourArtwork.nPublished', { done: shownPublished, total })}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center rounded-md bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground ring-1 ring-border">
        {required ? t('variantArtwork.statusNotUploaded') : t('platen.sideOptional')}
      </span>
    );
  })();

  // A platen slot states the exact pixels it expects before the drop (P3),
  // from the same numbers the pixel check uses (the org's, R4-5).
  const platenDims = platen ? (filePixels ?? expectedPlatenFilePixels(null, platen)) : null;
  const dropHint = platenDims
    ? t('platen.cellDropHint', { count: total, width: platenDims.width, height: platenDims.height, platen })
    : t('colourArtwork.dropHint', { count: total });

  // --- dropzone (empty + hard-error + resting-partial top-up) -------------
  const dropzone = (
    <label
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        void onFile(e.dataTransfer.files?.[0] ?? null);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      className={cn(
        'flex cursor-pointer flex-col items-center gap-1 rounded-md border-2 border-dashed px-3 py-4 text-center transition-colors duration-200 focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 motion-reduce:transition-none',
        dragOver ? 'border-threadheads-purple bg-threadheads-purple/5' : 'border-input hover:border-threadheads-purple/40',
      )}
    >
      <Upload className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      <span className="text-xs font-medium text-foreground">
        {side === 'front' ? t('variantArtwork.dropFront') : t('variantArtwork.dropBack')}
      </span>
      <span className="text-[11px] tabular-nums text-muted-foreground">{dropHint}</span>
      <input
        id={inputId}
        type="file"
        accept="image/png,.png"
        className="sr-only"
        // The wrapping label's visible text names the DROP action, not the
        // control; without this the input reaches a screen reader unnamed.
        aria-label={t('colourArtwork.fileInputAria', { side: sideLabel, colour: colourLabel })}
        onChange={(e) => {
          void onFile(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />
    </label>
  );

  // --- filled card (thumb + progress / rollup + actions) ------------------
  const replaceControl = admin && (
    <label
      aria-label={t('variantArtwork.replace')}
      title={t('variantArtwork.replace')}
      className="grid h-7 w-7 shrink-0 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors duration-200 hover:bg-muted hover:text-foreground focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1 motion-reduce:transition-none"
    >
      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
      <input
        id={`${inputId}-replace`}
        type="file"
        accept="image/png,.png"
        className="sr-only"
        aria-label={t('colourArtwork.replaceInputAria', { side: sideLabel, colour: colourLabel })}
        onChange={(e) => {
          void onFile(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />
    </label>
  );

  const filledCard = (
    <div className="flex items-start gap-3 rounded-md border bg-background px-3 py-2.5">
      {thumbUrl ? (
        <DesignThumb
          url={thumbUrl}
          filename={state.fileName ?? colourLabel}
          dark={dark}
          enlargeLabel={t('variantArtwork.enlarge', { side: sideLabel })}
          closeLabel={t('variantArtwork.close')}
        />
      ) : (
        <span className="grid h-16 w-16 shrink-0 place-items-center rounded-md border bg-muted text-muted-foreground">
          <ImageIcon className="h-6 w-6" aria-hidden="true" />
        </span>
      )}

      <div className="min-w-0 flex-1 space-y-1">
        {state.fileName && (
          <p className="truncate font-mono text-[11px] font-semibold" title={state.fileName}>
            {state.fileName}
          </p>
        )}

        {/* Round 4: this Small file was made from the colour's Large. */}
        {state.phase === 'idle' && smallFromLarge && (derivePhase === 'made' || smallFromLarge.made) && (
          <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary-strong ring-1 ring-primary/30">
            {t('platen.derive.madeChip')}
          </span>
        )}

        {/* Aggregate progress while the fan-out runs. */}
        {busy && (
          <div className="space-y-1" role="status" aria-live="polite">
            <div
              className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(progress * 100)}
              aria-label={t('colourArtwork.publishingProgress', { done: doneCount, total })}
            >
              <div
                className="h-full rounded-full bg-threadheads-purple transition-[width] duration-200 ease-out motion-reduce:transition-none"
                style={{ width: `${Math.round(progress * 100)}%` }}
              />
            </div>
            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground tabular-nums">
              {state.phase === 'hashing'
                ? t('variantArtwork.hashingLong')
                : t('colourArtwork.publishingProgress', { done: doneCount, total })}
            </p>
          </div>
        )}

        {/* Success — all SKUs published. */}
        {(isSuccess || (state.phase === 'idle' && restingFull)) && (
          <p className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="truncate">{t('colourArtwork.nPublished', { done: total, total })}</span>
          </p>
        )}

        {/* Partial — some SKUs still failed. Amber (recoverable), not red. */}
        {isPartial && (
          <div className="space-y-1.5">
            {/* Progress, not failure: a partial publish is a recoverable
                rollup with a retry sitting right below it. role="alert" is
                assertive and interrupts a screen reader mid-upload. */}
            <p className="flex items-start gap-1.5 text-[11px] text-amber-700" role="status">
              <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
              {t('colourArtwork.partialHint', { done: doneCount, total, failed: failedSkus.length })}
            </p>
            <Button
              size="sm"
              onClick={() => void retryFailed()}
              className="h-7 gap-1.5 bg-threadheads-purple px-2.5 text-[11px] text-white hover:bg-threadheads-purple/90"
            >
              <RefreshCw className="h-3 w-3" aria-hidden="true" />
              {t('colourArtwork.retryFailed')}
            </Button>
          </div>
        )}

        {/* Resting partial (server truth after reload) — top up via a re-drop. */}
        {state.phase === 'idle' && restingPartial && (
          <p className="text-[11px] text-amber-700">
            {t('colourArtwork.nPublished', { done: restingPublishedCount, total })}
          </p>
        )}
      </div>

      {(isSuccess || isPartial || (state.phase === 'idle' && (restingFull || restingPartial))) && replaceControl}
    </div>
  );

  // --- assemble body (state precedence) ----------------------------------
  let body: ReactNode;
  if (!admin) {
    // Read-only: server truth only; no upload affordance.
    if (restingFull || restingPartial) {
      body = filledCard;
    } else {
      body = <p className="text-[11px] text-muted-foreground">{t('colourArtwork.readOnlyNone')}</p>;
    }
  } else if (isHardError) {
    body = (
      <div className="space-y-2">
        {dropzone}
        {hardErrorMessage && (
          <div className="flex items-start justify-between gap-2">
            <p className="flex items-start gap-1.5 text-[11px] text-destructive" role="alert">
              <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
              {hardErrorMessage}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFileError(null);
                reset();
              }}
              className="h-6 shrink-0 px-2 text-[11px] text-muted-foreground"
            >
              {t('variantArtwork.dismiss')}
            </Button>
          </div>
        )}
      </div>
    );
  } else if (deriveLocked) {
    // The status line below announces it; this box only shows it. Waiting
    // its turn has no spinner: only making does.
    body = (
      <div className="flex min-h-[5.75rem] items-center justify-center gap-2 rounded-md border border-dashed bg-muted/40 px-3 py-4 text-center text-[11px] text-muted-foreground">
        {derivePhase === 'making' && (
          <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />
        )}
        {derivePhase === 'making' ? t('platen.derive.making') : t('platen.derive.waiting')}
      </div>
    );
  } else if (busy || isSuccess || isPartial || restingFull || restingPartial) {
    body = filledCard;
  } else if (smallFromLarge && !smallFromLarge.byHand) {
    // R4-12 default: a quiet panel, not a dropzone. A file dropped here is
    // still taken (it turns by hand on, in onFile); the choice itself is on
    // the platen's line above the grid.
    body = (
      <SmallMadePanel
        minH="min-h-[5.75rem]"
        lines={[
          t('platen.derive.defaultPanel'),
          ...(!smallFromLarge.largePublished ? [t('platen.derive.noLargeYet')] : []),
        ]}
        onDropFile={(file) => void onFile(file)}
      />
    );
  } else {
    body = dropzone;
  }

  // Round 4: the cell's Small made from Large actions (MUST 9). The by-hand
  // choice is on the Large row's line. Make Small from Large stays mounted
  // (aria-disabled, with a spinner) while it waits and runs, so focus stays
  // on it.
  // A Small cell keeps it mounted whenever its Small waits or is made; a
  // Large cell speaking for a hidden Small only after it was pressed there
  // (a make that follows a Large upload shows as a line instead).
  const [makePressed, setMakePressed] = useState(false);
  useEffect(() => {
    if (!deriveBusy) setMakePressed(false);
  }, [deriveBusy]);
  const showMake =
    admin &&
    !!derived &&
    !derived.byHand &&
    state.phase === 'idle' &&
    derivePhase !== 'failed' &&
    (smallFromLarge
      ? derived.onMake !== undefined || deriveBusy
      : deriveBusy
        ? makePressed
        : derived.onMake !== undefined);
  useEffect(() => {
    if (!showMake && makeFocusedRef.current) {
      makeFocusedRef.current = false;
      sectionRef.current?.focus();
    }
  }, [showMake]);
  useEffect(() => {
    if (!focusAfterRetryRef.current || derivePhase === 'failed') return;
    focusAfterRetryRef.current = false;
    (makeRef.current ?? sectionRef.current)?.focus();
  }, [derivePhase]);

  const keptLine = (cell: SmallFromLargeCell): string | null =>
    cell.state.phase === 'kept' && (cell.state.restCount ?? 0) > 0
      ? t('platen.derive.keptRest', { count: cell.state.restCount })
      : null;

  // Review round 3: 0 own SKUs kept means another product's own Small file holds the name.
  const keptWords = (cell: SmallFromLargeCell): string =>
    (cell.state.keptCount ?? 0) > 0
      ? t('platen.derive.kept', { count: cell.state.keptCount ?? 0 })
      : t('platen.derive.keptShared');

  let deriveNote: ReactNode = null;
  if (derived && admin && state.phase === 'idle' && !deriveLocked) {
    if (derivePhase === 'failed') {
      deriveNote = (
        <div className="space-y-1.5">
          <p className="flex items-start gap-1.5 text-[11px] text-destructive" role="alert">
            <AlertTriangle className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
            {derived.state.message}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              focusAfterRetryRef.current = true;
              derived.onRetry();
            }}
            className={cn(DERIVE_BUTTON_CLASS, 'gap-1.5')}
          >
            <RefreshCw className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t('platen.derive.tryAgain')}
          </Button>
        </div>
      );
    } else if (derived.ownFile) {
      deriveNote = (
        <p className="text-[11px] text-muted-foreground">
          {t('platen.derive.byHand.ownFile')}
          {keptLine(derived) && <> {keptLine(derived)}</>}
        </p>
      );
    } else if (derivePhase === 'kept') {
      deriveNote = (
        <p className="text-[11px] text-muted-foreground">
          {keptWords(derived)}
          {keptLine(derived) && <> {keptLine(derived)}</>}
        </p>
      );
    }
  } else if (smallFromLarge?.ownFile && (state.phase === 'idle' || isSuccess)) {
    deriveNote = <p className="text-[11px] text-muted-foreground">{t('platen.derive.byHand.ownFile')}</p>;
  }

  // S6b: a Large cell speaking for its hidden Small shows where that Small
  // is (a Small cell's locked box and chip do this for its own).
  let madeSmallLine: ReactNode = null;
  if (madeSmall && state.phase === 'idle') {
    if (deriveBusy && !showMake) {
      madeSmallLine = (
        <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
          {derivePhase === 'making' && (
            <Loader2 className="h-3 w-3 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          )}
          {derivePhase === 'making' ? t('platen.derive.making') : t('platen.derive.waiting')}
        </p>
      );
    } else if (derivePhase === 'made' || (derivePhase === 'idle' && madeSmall.made)) {
      madeSmallLine = (
        <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary-strong ring-1 ring-primary/30">
          <CheckCircle2 className="h-2.5 w-2.5 shrink-0" aria-hidden="true" />
          {t('platen.derive.smallMadeChip')}
        </span>
      );
    }
  }

  // One always-mounted polite status per cell that speaks for a Small made from Large.
  const deriveStatus = derived
    ? derivePhase === 'queued'
      ? t('platen.derive.waiting')
      : derivePhase === 'making'
        ? t('platen.derive.making')
        : derivePhase === 'made'
          ? t('platen.derive.madeSr')
          : derivePhase === 'kept'
            ? [keptWords(derived), keptLine(derived)]
                .filter(Boolean)
                .join(' ')
            : ''
    : '';

  return (
    <section
      ref={sectionRef}
      tabIndex={derived ? -1 : undefined}
      // Only a Small cell's own file is changing; a Large cell's status line speaks for its hidden Small.
      aria-busy={(!!smallFromLarge && derivePhase === 'making') || undefined}
      className={cn('space-y-2 outline-none', className)}
      aria-label={t('colourArtwork.regionAria', { side: sideLabel, colour: colourLabel })}
    >
      {/* Side label + status chip — mirrors VariantArtworkUploader; on md+ the
          column header already names the side, but the chip carries state. */}
      <header className="flex items-center justify-between gap-2 md:hidden">
        <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{sideLabel}</span>
        {statusChip}
      </header>
      <div className="hidden justify-center md:flex">{statusChip}</div>

      {body}
      {(madeSmallLine || (showMake && derived) || deriveNote) && (
        <div className="flex flex-col items-center gap-1.5 text-center">
          {madeSmallLine}
          {showMake && derived && (
            <Button
              ref={makeRef}
              size="sm"
              aria-disabled={deriveBusy || undefined}
              onFocus={() => (makeFocusedRef.current = true)}
              onBlur={() => (makeFocusedRef.current = false)}
              onClick={() => {
                if (deriveBusy) return;
                setMakePressed(true);
                derived.onMake?.();
              }}
              className={cn(
                DERIVE_BUTTON_CLASS,
                'gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90',
                deriveBusy && 'cursor-not-allowed opacity-70 hover:bg-threadheads-purple',
              )}
            >
              {deriveBusy && <Loader2 className="h-3 w-3 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden="true" />}
              {t('platen.derive.make')}
            </Button>
          )}
          {deriveNote}
        </div>
      )}
      {placeOnPlaten && (
        <div className="flex justify-center">
          <StudioEntryLink
            productId={product.id}
            label={t('entry.placeOnPlaten', { ns: 'uploadStudio' })}
            focus={placeOnPlaten.focus}
            onReturn={placeOnPlaten.onReturn}
          />
        </div>
      )}
      {derived && (
        <span className="sr-only" role="status" aria-live="polite">
          {deriveStatus}
        </span>
      )}

      {/* Second-side (back) print-delivery note (BLU-663), matching the single
          cell, unless the grid shows it once for all its rows. */}
      {showBackNote && side === 'back' && !isHardError && (
        <p className="text-[11px] text-muted-foreground">{t('variantArtwork.backSideNote')}</p>
      )}
    </section>
  );
}
